import { useEffect, useSyncExternalStore } from "react";

/**
 * Tracks whether something other than the app root has mounted its own sonner
 * <Toaster />.
 *
 * This exists because of one specific collision. omniflow-ui renders its own
 * <Toaster /> inside the component we mount for the flow builder, sonner is
 * externalised from its bundle so both packages resolve the *same* module, and
 * every mounted Toaster renders every toast. Two Toasters means every toast
 * appears twice — verified, not assumed.
 *
 * Suppressing ours loses nothing: because the observer is shared, the surviving
 * Toaster renders the client's toasts too.
 *
 * Keyed on the actual mount rather than on the route, because "is the builder on
 * screen" is the real condition; a route check would be a second place to
 * remember when omniflow-ui moves.
 */
let externalToasters = 0;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const notify = () => listeners.forEach((listener) => listener());

/** Declare that this subtree mounts a Toaster of its own. */
export const useOwnToaster = () => {
  useEffect(() => {
    externalToasters += 1;
    notify();
    return () => {
      externalToasters -= 1;
      notify();
    };
  }, []);
};

export const useHasExternalToaster = () =>
  useSyncExternalStore(
    subscribe,
    () => externalToasters > 0,
    () => false,
  );
